import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import LoginPage from './components/LoginPage';
import Layout from './components/Layout';
import Dashboard from './screens/Dashboard';
import Grupos from './screens/Grupos';
import Usuarios from './screens/Usuarios';
import PlayerList from './screens/PlayerList';
import CrearJugador from './screens/CrearJugador';
import PlayerProfile from './screens/PlayerProfile';
import BigBoard from './screens/BigBoard';
import Tags from './screens/Tag';
import InstatTags from './screens/InstatTags';
import PlayerCardCreator from './screens/PlayerCardCreator';
import RechercheInforme from './screens/RechercheInforme'; 
import RechercheIntel from './screens/RechercheIntel';
import BigBoardTable from './screens/BigBoardTable';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import BigBoardModif from './screens/BigBoardModif';
import Importar from './screens/Importar';
import Tecnification from './screens/Tecnification';
import Seguimiento from './screens/Seguimiento';
import SeguimientoTable from './screens/SeguimientoTable';
import SeguimientoModif from './screens/SeguimientoModif';
import News from './screens/News';
import ResumeJournee from './components/ResumeJournee';
import ColorRange from './components/ColorRange';

import Cantera from './cantera/Cantera';
import AccesCantera from './cantera/AccesCantera'; 
import Formulario from './cantera/Formulario';
import TestLecteur from './cantera/TestLecteur';
import Agencia from './screens/Agencia';
import Agenda from './screens/Agenda/Agenda';
import AgendaTags from './screens/Agenda/AgendaTags';
import CanteraTags from './screens/CanteraTags';

import TabsContainerRankSegui from './components/TabsContainerRankSegui';
import TabsContainerInforme from './components/TabsContainerInforme';

import AllTechniquesTable from './components/AllTechniquesTable';
import VideoGallery from './components/VideoGallery';
import AllMedidasTable from './components/AllMedidasTable';
import AllMedicalTable from './components/AllMedicalTable';

import CompetitionsManagement from './screens/Competitions/CompetitionsManagement';
import CompetitionsViewer from './screens/Competitions/CompetitionsViewer';

import PlayersWithoutTeam from './screens/PlayersWithoutTeam';
import InstatPOC from './screens/Instat/InstatPOC';

// Import des providers
import { BigBoardProvider } from './BigBoardContext';
import { BigBoardTableProvider } from './BigBoardTableContext';
import { SeguimientoProvider } from './SeguimientoContext';
import { SeguimientoTableProvider } from './SeguimientoTableContext';
import { RechercheInformeProvider } from './screens/RechercheInformeContext';
import { RechercheIntelProvider } from './screens/RechercheIntelContext';
import { TabsContainerInformeProvider } from './components/TabsContainerInformeContext';
import { ClubThemeProvider } from './contexts/ClubThemeContext';
import AppThemeProvider from './theme/AppThemeProvider';
import { changeLanguage } from './i18n';

// Composant pour regrouper tous les providers
const AppProviders = ({ children }) => {
  return (
    <ClubThemeProvider>
      <AppThemeProvider>
      <BigBoardProvider>
        <BigBoardTableProvider>
          <SeguimientoProvider>
            <SeguimientoTableProvider>
              <RechercheInformeProvider>
                <RechercheIntelProvider>
                  <TabsContainerInformeProvider>
                    {children}
                  </TabsContainerInformeProvider>
                </RechercheIntelProvider>
              </RechercheInformeProvider>
            </SeguimientoTableProvider>
          </SeguimientoProvider>
        </BigBoardTableProvider>
      </BigBoardProvider>
      </AppThemeProvider>
    </ClubThemeProvider>
  );
};

// Composant pour les routes authentifiées
const AuthenticatedRoutes = ({ onLogout }) => {

  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    // Restaurer le mode demo après rafraîchissement de page
    if (userData?.is_demo) {
      document.body.classList.add('demo-mode');
    }
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Layout onLogout={onLogout} />}>
        {/* Route par défaut qui redirige vers le dashboard */}
        <Route 
          index 
          element={
            user?.role === 'entrenador' || user?.role === 'scout interne' || user?.role === 'scout externe' || user?.role === 'scout interne cantera' || user?.role === 'scout externe cantera' ?
              <Navigate to="/jugadores" replace /> :
              <Dashboard />
          } 
        />
        <Route index element={<Dashboard />} />
        <Route path="jugadores" element={<PlayerList />} />
        <Route path="menu" element={<Dashboard />} />
        <Route path="grupos" element={<Grupos />} />
        <Route path="usuarios" element={<Usuarios />} />
        <Route path="big-board" element={<BigBoard />} />
        <Route path="crear-jugador" element={<CrearJugador />} />
        <Route path="tags" element={<Tags />} />
        <Route path="instat-tags" element={<InstatTags />} />
        <Route path="crear-ficha" element={<PlayerCardCreator />} />
        <Route path="buscar-informe" element={<RechercheInforme />} />
        <Route path="buscar-intel" element={<RechercheIntel />} />
        <Route path="buscar-bigboard" element={<BigBoardTable />} />
        <Route path="bigboard/edit/:id" element={<BigBoardModif />} />
        <Route path="importar" element={<Importar />} />
        <Route path="tecnification" element={<Tecnification />} />
        <Route path="/players/:id" element={<PlayerProfile />} />
        <Route path="seguimiento" element={<Seguimiento />} />
        <Route path="buscar-seguimiento" element={<SeguimientoTable />} />
        <Route path="seguimiento/edit/:id" element={<SeguimientoModif />} />
        <Route path="news" element={<News />} />
        <Route path="resume" element={<ResumeJournee />} />
        <Route path="agencia" element={<Agencia />} />
        <Route path="cantera/:groupeId" element={<Cantera />} />
        <Route path="agenda" element={<Agenda />} />
        <Route path="agenda-tags" element={<AgendaTags />} />
        <Route path="cantera-tags" element={<CanteraTags />} />
        <Route path="range" element={<ColorRange />} />

        <Route path="/scouting" element={<TabsContainerRankSegui />} />
        <Route path="/informes" element={<TabsContainerInforme />} />

        <Route path="/techniques/all" element={<AllTechniquesTable />} />
        <Route path="/videos" element={<VideoGallery />} />
        <Route path="/medidas/all" element={<AllMedidasTable />} />
        <Route path="/medical/all" element={<AllMedicalTable />} />

        <Route path="/competitions-management" element={<CompetitionsManagement />} /> 
        <Route path="/competitions-vue" element={<CompetitionsViewer />} />
        <Route path="/competitions-performance" element={<Navigate to="/competitions-vue" replace />} />
        <Route path="/players-without-team" element={<PlayersWithoutTeam />} />
        <Route path="/instat-poc" element={<InstatPOC />} />


      </Route>
    </Routes>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('clubId');
    localStorage.removeItem('clubSlug');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('language');
    localStorage.removeItem('clubTheme'); // Nettoyer le thème du club
    localStorage.removeItem('clubConfig');
    localStorage.removeItem('disclaimerAccepted_v1');
    document.body.classList.remove('demo-mode'); // Nettoyer le mode demo
    // Remettre la langue par défaut (espagnol)
    changeLanguage('es');
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppProviders>
      <Router>
        <Routes>
          {/* Route d'accès public pour AccesCantera */}
          <Route path="/acces" element={<AccesCantera />} />
          <Route path="/formulario/:accesmail" element={<Formulario />} />
          <Route path="/testlecteur" element={<TestLecteur />} />
          {/* Route de connexion */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />

          {/* Routes authentifiées */}
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <AuthenticatedRoutes onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </AppProviders>
  );
}

export default App;